import React, { type ReactNode, useState, useCallback, useEffect, useMemo } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import Heading from '@atlaskit/heading';
import Lozenge from '@atlaskit/lozenge';
import { Flex, Box, xcss, Text } from '@atlaskit/primitives';

import Spinner from '@atlaskit/spinner';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { fontSize } from '@atlassian/jira-common-styles/src';
import { ff } from '@atlassian/jira-feature-flagging';
import { useIntl } from '@atlassian/jira-intl';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';

import { isSynthetic } from '@atlassian/jira-project-create-importer-common';
import {
	LEARN_MORE_ABOUT_OTHER_TOOLS_LINK,
	OAUTH_UNSUCCESS_FLAG_ID,
} from '../../../common/constants';
import { AccessTokenModalType } from '../../../common/constants/access-token';
import type { ImportType, SupportedThirdParty } from '../../../common/constants/import-type';
import type { Tool } from '../../../common/constants/tools';
import { renderIcon } from '../../../common/ui/icon';
import { useContentForThirdParty } from '../../../common/utils';
import { useProjectSpecificContent } from '../../../common/utils/project-specific-content';
import { useX2CFlag } from '../../../common/utils/use-flag';
import { useProjectData } from '../../../controllers/import-state';
import { useSource } from '../../../controllers/import-state/selectors';
import { useAccessTokenModalState } from '../../../controllers/ui-state';
import { useTools } from '../../../controllers/utils';
import { useThirdPartyOAuth } from '../../../services/start-third-party-oauth-flow';
import { useThirdPartyAccessTokenStatus } from '../../../services/third-party-access-token-status';
import messages from './messages';
import { AsyncRequestOtherToolsModal } from './other-tools-request-modal/async';
import PreOAuthModal from './pre-oauth-modal';

type Props = {
	onNext: (importType?: ImportType) => void;
};

export const ImportFromOtherTools = ({ onNext }: Props) => {
	const [isRequestOtherToolModalOpen, setRequestOtherToolModalOpen] = useState(false);
	const [preOAuthModalStatus, setPreOAuthModalStatus] = useState<{
		isModalOpen: boolean;
		onResolve?: (value: boolean) => boolean;
	}>({ isModalOpen: false });
	const [showFlag, setShowFlag] = useState(false);

	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { supportedTools, unavailableTools } = useTools();
	const startThirdPartyOAuth = useThirdPartyOAuth();
	const [, { setRequestAuthenticationToken }] = useAccessTokenModalState();
	const { isLoading, validateAccessToken } = useThirdPartyAccessTokenStatus({
		requestAuthenticationToken: setRequestAuthenticationToken,
	});
	const [selectedThirdParty, setSelectedThirdParty] = useState<SupportedThirdParty | undefined>(
		undefined,
	);
	const { onShowFlag, onDismissFlag } = useX2CFlag();
	const { oAuthUnsuccessFlagTitle, oAuthUnsuccessFlagDescription } =
		useContentForThirdParty(selectedThirdParty);

	const [{ type }] = useProjectData();
	const [source] = useSource();
	const {
		importFromOtherAppsHeading,
		importFromOtherAppsHelperMessage,
		importFromOtherAppsLearnMoreOtherApps,
	} = useProjectSpecificContent(type, source);

	const onClickForOtherTools = useCallback(() => {
		setRequestOtherToolModalOpen(true);
		const analyticsEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'otherToolsButton',
		});
		fireUIAnalytics(analyticsEvent);
	}, [createAnalyticsEvent]);

	const showPreOAuthModal = useCallback(() => {
		let resolve = (value: boolean) => value;
		const promise = new Promise((resolveProp: (value: boolean) => void) => {
			resolve = (value) => {
				resolveProp(value);
				return value;
			};
		});
		setPreOAuthModalStatus({
			isModalOpen: true,
			onResolve: resolve,
		});
		return promise;
	}, []);

	const onCloseForPreOAuthModal = useCallback(() => {
		preOAuthModalStatus.onResolve && preOAuthModalStatus.onResolve(false);
		setPreOAuthModalStatus({
			isModalOpen: false,
		});
	}, [preOAuthModalStatus]);

	const onNextForPreOAuthModal = useCallback(() => {
		preOAuthModalStatus.onResolve && preOAuthModalStatus.onResolve(true);
	}, [preOAuthModalStatus]);

	const onOAuthFail = useCallback(() => {
		setPreOAuthModalStatus({ isModalOpen: false });
		setShowFlag(true);
	}, []);

	useEffect(() => {
		if (showFlag) {
			setShowFlag(false);
			oAuthUnsuccessFlagTitle &&
				onShowFlag(OAUTH_UNSUCCESS_FLAG_ID, {
					title: oAuthUnsuccessFlagTitle,
					description: [
						oAuthUnsuccessFlagDescription,
						{
							link: (children: ReactNode) => (
								<a href="/secure/ContactAdministrators!default.jspa" target="_blank">
									{children}
								</a>
							),
						},
					],
				});
		}
	}, [
		formatMessage,
		oAuthUnsuccessFlagDescription,
		oAuthUnsuccessFlagTitle,
		onShowFlag,
		selectedThirdParty,
		showFlag,
	]);

	const handleSupportedToolClick = useCallback(
		(tool: Tool) => async () => {
			const analyticsEvent = createAnalyticsEvent({
				action: 'clicked',
				actionSubject: `${tool.key}Button`,
			});
			fireUIAnalytics(analyticsEvent);
			onDismissFlag(OAUTH_UNSUCCESS_FLAG_ID);
			if (tool.isSupported) {
				selectedThirdParty !== tool.key && setSelectedThirdParty(tool.key);

				if (ff('suppress-oauth-for-3p-apps-on-pollinator-tenants_5vbjr') && isSynthetic()) {
					onNext(tool.key);
					return;
				}

				if (tool.requiresOAuth) {
					if (tool.showPreOAuthModal) {
						const continueOAuth = await showPreOAuthModal();
						if (continueOAuth === false) {
							return;
						}
					}
					startThirdPartyOAuth({
						thirdParty: tool.key,
						onSuccess: onNext,
						onFail: onOAuthFail,
					});
					return;
				}
				if (tool.requiresPersonalAccessToken) {
					validateAccessToken(tool.key).then((status) => {
						if (status === 'VALID') {
							onNext(tool.key);
						} else {
							setRequestAuthenticationToken({
								thirdParty: tool.key,
								modalType: AccessTokenModalType.ADD_NEW_TOKEN,
							}).then((isSaved) => {
								if (isSaved) {
									onNext(tool.key);
								}
							});
						}
					});

					return;
				}
				onNext(tool.key);
			}
		},
		[
			createAnalyticsEvent,
			onDismissFlag,
			onNext,
			onOAuthFail,
			setRequestAuthenticationToken,
			selectedThirdParty,
			showPreOAuthModal,
			startThirdPartyOAuth,
			validateAccessToken,
		],
	);

	const renderDescription = useMemo(() => {
		if (importFromOtherAppsLearnMoreOtherApps) {
			return formatMessage(importFromOtherAppsHelperMessage, {
				link: (
					<a href={LEARN_MORE_ABOUT_OTHER_TOOLS_LINK} target="_blank">
						{formatMessage(importFromOtherAppsLearnMoreOtherApps)}
					</a>
				),
			});
		}
		return formatMessage(importFromOtherAppsHelperMessage);
	}, [formatMessage, importFromOtherAppsHelperMessage, importFromOtherAppsLearnMoreOtherApps]);

	const renderUnavailableTools = useCallback(
		() => (
			<>
				<UnavailableToolsWrapper>
					{unavailableTools.map((tool: Tool) => (
						<OtherToolWrapper key={tool.key}>
							{renderIcon(tool.icon)}
							<ToolNameWrapper>
								<p>{tool.name}</p>
							</ToolNameWrapper>
							{tool.label && (
								<Text size="small" color="color.text.subtle">
									{tool.label}
								</Text>
							)}
						</OtherToolWrapper>
					))}
				</UnavailableToolsWrapper>
				<OtherAppsDescription data-testid="project-create-importer-upload-flow.ui.upload-screen.import-from-other-tools.other-apps-description">
					{formatMessage(messages.otherApps, {
						link: (
							<StyledLinkButton
								appearance="link"
								onClick={onClickForOtherTools}
								testId="project-create-importer-upload-flow.ui.upload-screen.import-from-other-tools.button"
							>
								{formatMessage(messages.tellUsWhere)}
							</StyledLinkButton>
						),
					})}
				</OtherAppsDescription>
			</>
		),
		[formatMessage, onClickForOtherTools, unavailableTools],
	);

	return (
		<Wrapper data-testid="project-create-importer-upload-flow.ui.upload-screen.import-from-other-tools.wrapper">
			{supportedTools.length > 0 && (
				<>
					<HeadingWrapper>
						<Heading size="medium" as="h2">
							{formatMessage(messages.supportedAppsHeading)}
						</Heading>
					</HeadingWrapper>
					<ButtonGroup>
						{supportedTools.map((tool: Tool) => (
							<OtherToolsButton
								key={tool.key}
								onClick={handleSupportedToolClick(tool)}
								aria-label={tool.name}
							>
								{selectedThirdParty === tool.key &&
								(isLoading || preOAuthModalStatus.isModalOpen) ? (
									<Spinner size="small" />
								) : (
									<ButtonContentWrapper>
										{renderIcon(tool.icon)}
										<ButtonHeader level="h500" as="h4">
											{tool.name}
										</ButtonHeader>
										<Text size="medium" color="color.text.subtle">
											{tool.label}
										</Text>
									</ButtonContentWrapper>
								)}
							</OtherToolsButton>
						))}
					</ButtonGroup>
					<HorizontalSeperator />
				</>
			)}
			<HeadingWrapper>
				<Flex alignItems="center">
					<Heading size="medium" as="h2">
						{formatMessage(importFromOtherAppsHeading)}
					</Heading>
					<Box xcss={lozengeWrapperStyles}>
						<Lozenge appearance="new">{formatMessage(messages.comingSoon)}</Lozenge>
					</Box>
				</Flex>
				<Description data-testid="project-create-importer-upload-flow.ui.upload-screen.import-from-other-tools.description">
					{renderDescription}
				</Description>
			</HeadingWrapper>

			{renderUnavailableTools()}
			<AsyncRequestOtherToolsModal
				isModalOpen={isRequestOtherToolModalOpen}
				setIsModalOpen={setRequestOtherToolModalOpen}
			/>
			{selectedThirdParty && (
				<PreOAuthModal
					isOpen={preOAuthModalStatus.isModalOpen}
					onClose={onCloseForPreOAuthModal}
					onNext={onNextForPreOAuthModal}
					thirdParty={selectedThirdParty}
				/>
			)}
		</Wrapper>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div({
	padding: token('space.400', '32px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('elevation.surface', colors.N0),
	marginTop: token('space.400', '32px'),
	marginBottom: token('space.400', '32px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonGroup = styled.div({
	display: 'flex',
	flexFlow: 'row wrap',
	gap: `${token('space.200', '16px')} ${token('space.200', '16px')}`,
	marginTop: token('space.200', '16px'),
});

type ButtonProps = {
	disabled: boolean;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const OtherToolsButton = styled.button<any>(
	{
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexDirection: 'column',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		backgroundColor: token('elevation.surface', colors.N0),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		border: `solid 1px ${token('color.border', colors.N30)}`,
		boxShadow: token(
			'elevation.shadow.raised',
			'0px 1px 1px rgba(9, 30, 66, 0.25),0px 0px 1px rgba(9, 30, 66, 0.31)',
		),
		borderRadius: '8px',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		cursor: ({ disabled }: ButtonProps) => (disabled ? 'not-allowed' : 'pointer'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ disabled }: ButtonProps) => (disabled ? 'opacity: 0.5;' : ''),
	{
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		fontSize: `${fontSize}px`,
		width: '200px',
		padding: token('space.025', '2px'),
		height: '152px',
		'&:hover': {
			boxShadow: token(
				'elevation.shadow.overflow',
				'0px 0px 8px rgba(9, 30, 66, 0.16),0px 0px 1px rgba(9, 30, 66, 0.12)',
			),
		},
	},
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonContentWrapper = styled.div({
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'center',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonHeader = styled(Heading)({
	marginTop: token('space.050', '8px'),
	marginBottom: token('space.050', '4px'),
});

const lozengeWrapperStyles = xcss({
	marginLeft: 'space.200',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeadingWrapper = styled.div({
	margin: token('space.100', '8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HorizontalSeperator = styled.hr({
	border: 'none',
	height: '1px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.border', colors.N40),
	margin: `${token('space.300', '24px')} 0px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Description = styled.p({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: `${token('color.text.subtlest', colors.N400)}`,
	marginTop: token('space.200', '16px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const UnavailableToolsWrapper = styled.div({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'baseline',
	marginTop: token('space.400', '32px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const OtherToolWrapper = styled.figure({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	margin: `0 ${token('space.500', '40px')} 0 0`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ToolNameWrapper = styled.figcaption({
	marginTop: token('space.100', '8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const OtherAppsDescription = styled(Description)({
	marginTop: token('space.500', '40px'),
	display: 'flex',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledLinkButton = styled(Button)({
	padding: '0',
});
